import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, Loader2, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { canSeeMPress } from "../lib/mpressVisibility";

const AVATAR_SRC = "/mpress-avatar.png";

/**
 * MPress — site-wide AI concierge floating chat widget.
 * Renders a bottom-right pill button; opens a chat panel with message history,
 * a text input, and a reset button. Session id + history persist in localStorage
 * so the conversation survives navigation and refreshes.
 */
const STORAGE_KEY = "mpress:v1";

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.messages)) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

function saveState(sessionId, messages) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, messages }));
  } catch {
    /* ignore */
  }
}

const WELCOME = {
  role: "assistant",
  text:
    "Hey there — I'm MPress, your MPire concierge. Ask me about products, how to become a wholesale partner, or where to find something on the site.",
};

// Render assistant text with clickable `/paths` and bare URLs. Plain-text only otherwise
// (we deliberately don't run markdown to keep the widget lean and safe).
function renderReply(text, onInternalNav) {
  const parts = [];
  const regex = /(`\/[a-zA-Z0-9\-_/:]+`|https?:\/\/[^\s]+)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith("`/")) {
      const path = token.slice(1, -1);
      parts.push(
        <a
          key={`p-${key++}`}
          href={path}
          onClick={(e) => { e.preventDefault(); onInternalNav(path); }}
          className="text-amber-600 underline decoration-amber-300 underline-offset-2 hover:text-amber-700"
        >
          {path}
        </a>
      );
    } else {
      parts.push(
        <a
          key={`p-${key++}`}
          href={token}
          target="_blank"
          rel="noopener noreferrer"
          className="text-amber-600 underline decoration-amber-300 underline-offset-2 hover:text-amber-700"
        >
          {token}
        </a>
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
}

export default function MPressChatWidget() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const s = loadState();
    return s?.messages?.length ? s.messages : [WELCOME];
  });
  const [sessionId, setSessionId] = useState(() => loadState()?.sessionId || null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    saveState(sessionId, messages);
  }, [sessionId, messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    const nextMsgs = [...messages, { role: "user", text }];
    setMessages(nextMsgs);
    try {
      const { data } = await api.post("/mpress/chat", {
        message: text,
        session_id: sessionId || undefined,
      });
      setSessionId(data.session_id);
      setMessages((cur) => [...cur, { role: "assistant", text: data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Network hiccup — try again in a moment.";
      setMessages((cur) => [
        ...cur,
        { role: "assistant", text: `Sorry — ${detail}` },
      ]);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    try {
      if (sessionId) await api.post("/mpress/reset", { session_id: sessionId, message: "reset" });
    } catch {
      /* ignore */
    }
    setSessionId(null);
    setMessages([WELCOME]);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (loading || !canSeeMPress(user)) return null;

  return (
    <>
      {!open && (
        <button
          data-testid="mpress-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-3 z-40 flex items-center gap-2 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 p-1 pr-4 text-white shadow-2xl shadow-amber-600/30 ring-2 ring-amber-400/40 transition-transform hover:scale-105 hover:shadow-amber-600/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 sm:bottom-5 sm:right-5 sm:z-[60]"
          aria-label="Open MPress concierge"
        >
          <img
            src={AVATAR_SRC}
            alt="MPress"
            className="h-10 w-10 rounded-full border border-amber-300 object-cover shadow-inner"
          />
          <span className="hidden text-sm font-semibold tracking-wide sm:inline">MPress</span>
        </button>
      )}

      {open && (
        <>
          <button
            aria-label="Close MPress concierge"
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] sm:hidden"
            data-testid="mpress-mobile-backdrop"
            onClick={() => setOpen(false)}
          />
        <div
          data-testid="mpress-panel"
          role="dialog"
          aria-label="MPress concierge"
          aria-modal="true"
          className="fixed inset-x-2 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] top-[calc(0.75rem+env(safe-area-inset-top))] z-[60] flex w-auto max-w-none flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-2xl sm:inset-x-auto sm:top-auto sm:bottom-5 sm:right-5 sm:h-[560px] sm:max-h-[85vh] sm:w-[400px] sm:max-w-[92vw] sm:rounded-2xl"
        >
          <div className="flex items-center justify-between border-b border-neutral-200 bg-gradient-to-br from-amber-500 to-amber-700 px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              <img
                src={AVATAR_SRC}
                alt="MPress"
                className="h-11 w-11 rounded-full border-2 border-amber-200 object-cover shadow-md"
              />
              <div>
                <div className="text-sm font-semibold leading-tight">MPress</div>
                <div className="text-[11px] uppercase tracking-widest text-amber-100/90">MPire concierge</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="mpress-reset"
                onClick={reset}
                title="Reset conversation"
                className="rounded-md p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                data-testid="mpress-close"
                onClick={() => setOpen(false)}
                title="Close"
                className="rounded-md p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            data-testid="mpress-messages"
            className="flex-1 space-y-3 overflow-y-auto bg-neutral-50 px-4 py-4"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "assistant" && (
                  <img
                    src={AVATAR_SRC}
                    alt="MPress"
                    className="h-7 w-7 flex-none rounded-full border border-amber-300 object-cover shadow-sm"
                  />
                )}
                <div
                  className={`max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm leading-relaxed shadow-sm ${
                    m.role === "user"
                      ? "rounded-br-sm bg-neutral-900 text-white"
                      : "rounded-bl-sm border border-neutral-200 bg-white text-neutral-800"
                  }`}
                >
                  {m.role === "assistant" ? renderReply(m.text, (path) => { setOpen(false); navigate(path); }) : m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-end gap-2">
                <img
                  src={AVATAR_SRC}
                  alt="MPress"
                  className="h-7 w-7 flex-none rounded-full border border-amber-300 object-cover shadow-sm"
                />
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-500 shadow-sm">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  MPress is thinking…
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-neutral-200 bg-white px-3 py-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="mpress-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                maxLength={2000}
                placeholder="Ask MPress anything about MPire…"
                className="max-h-32 flex-1 resize-none rounded-xl border border-neutral-300 bg-neutral-50 px-3 py-2 text-sm text-neutral-800 placeholder:text-neutral-400 focus:border-amber-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
              <button
                data-testid="mpress-send"
                onClick={send}
                disabled={sending || !input.trim()}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-neutral-400">
              <span>AI concierge · MPire-only knowledge</span>
              <span>{input.length}/2000</span>
            </div>
          </div>
        </div>
        </>
      )}
    </>
  );
}
